/**
 * Il rendimento anno per anno, col mercato fra parentesi.
 *
 * E' l'informazione che nessun totale riassume. Una configurazione su XRP chiude a +15.445%, e
 * il numero da solo non dice la cosa che conta: che nel 2022, con il mercato a −60%, ha fatto
 * +12%. Un'altra con lo stesso totale potrebbe aver perso il quaranta per cento in quell'anno e
 * recuperato dopo. Sono due strategie diverse e il totale le mostra identiche.
 *
 * ## La lettura: tre segnali indipendenti, leggibili insieme
 *
 * - il COLORE DEL TESTO dice il segno della strategia -- verde se ha guadagnato, rosso se ha
 *   perso. Appartiene alla strategia e a nient'altro;
 * - il GRASSETTO dice se ha battuto il mercato, indipendentemente dal segno;
 * - il VELO DELLA RIGA dice la stessa cosa del grassetto ma la rende visibile da lontano, e in
 *   piu' ne dice l'entita': verde se ha battuto il mercato, rosso se ha fatto peggio, con
 *   intensita' proporzionale al distacco.
 *
 * Colore del testo e velo restano DUE segnali distinti perche' rispondono a due domande diverse.
 * L'anno che si cerca guardando questa lista e' quello con testo rosso su velo verde: «ha perso,
 * ma molto meno del mercato» -- il 2022 con la strategia a −10% e il mercato a −60%. Un colore
 * unico ne perderebbe una delle due, e senza velo la differenza fra un anno vinto di un punto e
 * uno vinto di duecento non si vede scorrendo.
 *
 * Il grassetto non e' ridondante col velo: e' il segnale che sopravvive alla stampa in bianco e
 * nero e a chi non distingue verde da rosso.
 *
 * Il numero fra parentesi e' il mercato: sta li' perche' un rendimento senza il suo metro non si
 * puo' giudicare, e perche' cosi' non serve passare il mouse su niente -- su telefono quel gesto
 * non esiste, e questa lista si guarda soprattutto da telefono.
 */
import type { CSSProperties } from 'react';
import type { RendimentoAnnuale } from '../api/tipi';

type Riga = { anno: string; strategia: number; mercato: number | null };

/** Scarta gli anni senza misura invece di mostrarli a zero: zero e' un'affermazione. */
function leggi(anni: RendimentoAnnuale[] | null | undefined): Riga[] {
  if (!Array.isArray(anni)) return [];
  return anni
    .filter((a) => a && typeof a.change === 'number' && /^\d{4}$/.test(String(a.year)))
    .map((a) => ({
      anno: String(a.year),
      strategia: a.change as number,
      mercato: typeof a.marketChange === 'number' ? a.marketChange : null,
    }));
}

/** Due decimali come nell'interfaccia precedente, col segno sempre esplicito. */
function pc(v: number): string {
  return `${v >= 0 ? '+' : '−'}${Math.abs(v).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function AnniControMercato({
  anni,
  densita = 'compatta',
}: {
  anni: RendimentoAnnuale[] | null | undefined;
  densita?: 'compatta' | 'ariosa';
}) {
  const righe = leggi(anni);
  if (!righe.length) {
    return (
      <span style={{ fontSize: 'var(--sb-t-12)', color: 'var(--sb-testo-tenue)' }}>
        rendimenti annuali non disponibili
      </span>
    );
  }

  /* Il distacco piu' grande della serie e' il metro con cui si gradua il velo di ogni anno.
     Si normalizza sulla serie e non su un valore fisso perche' un +244 punti e un +3 punti
     sono entrambi «il migliore» nella loro serie, e il confronto che interessa e' interno. */
  const distaccoMassimo = Math.max(
    0,
    ...righe.map((r) => (r.mercato === null ? 0 : Math.abs(r.strategia - r.mercato))),
  );

  return (
    <div className={`sb-anni ${densita === 'ariosa' ? 'sb-anni--ariosa' : ''}`}>
      {righe.map((r) => {
        // Battuto il mercato: il confronto vale anche fra due numeri negativi, dove perdere
        // meno e' vincere. Senza mercato misurato non si afferma niente.
        const distacco = r.mercato === null ? null : r.strategia - r.mercato;
        const battuto = distacco !== null && distacco >= 0;
        /* Radice del distacco, normalizzata sul massimo: senza comprimere, un anno a +244
           punti porterebbe la sua riga al massimo e lascerebbe tutte le altre a un velo
           invisibile. E' una gradazione per confrontare a colpo d'occhio, non per misurare --
           il numero esatto e' scritto sulla riga stessa. */
        const forza =
          distacco === null || distaccoMassimo === 0
            ? 0
            : Math.min(1, Math.sqrt(Math.abs(distacco)) / Math.sqrt(distaccoMassimo));
        const velo =
          distacco === null ? '' : battuto ? 'sb-anni__riga--sopra' : 'sb-anni__riga--sotto';
        return (
          <div
            key={r.anno}
            className={`sb-anni__riga ${velo}`}
            /* L'intensita' passa al CSS come numero puro: il colore resta un token e non
               viene ricostruito qui, dove diventerebbe una copia dei canali RGB. */
            style={{ '--sb-anni-forza': forza } as CSSProperties}
          >
            <span className="sb-anni__anno">{r.anno}</span>
            <span
              className={`sb-anni__valore ${battuto ? 'sb-anni__valore--battuto' : ''}`}
              style={{ color: r.strategia < 0 ? 'var(--sb-perdita)' : 'var(--sb-guadagno)' }}
            >
              {pc(r.strategia)}
              <span className="sb-anni__mercato">
                {' '}
                ({r.mercato === null ? '—' : pc(r.mercato)})
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
